import * as types from "./authConstants"
import { produce } from 'immer';
import { IUser } from "../../Types/user";
import { ActionRedux } from "../../Types/actionRedux";
import { TokenExpiration } from "../../utils/tokenExpiration";
import { FirebaseAuthService } from "../../Services/FirebaseAuthService";
import { buildInitialAuthState, emptyAuthUser } from "./authInitialState";

const initialState = {
  ...buildInitialAuthState(),
  authMethod: null as string | null,
};

export const authReducer = (state = initialState, action: ActionRedux) =>
  produce(state, draft => {
    switch (action.type) {
      case types.AUTH_LOADING:
        draft.loading = true;
        break;
      case types.AUTH_INIT_COMPLETE:
        draft.loading = false;
        break;
      case types.USER_LOADED:
      case types.LOGIN_SUCCESS:
      case types.REGISTER_SUCCESS:
        localStorage.setItem('user', JSON.stringify(action.payload));
        draft.isAuthenticated = true;
        draft.loading = false;
        draft.token = action.payload.access_token;
        draft.user = action.payload;
        draft.authMethod = "traditional";
        draft.loginFailed = false;
        // Start monitoring token expiration
        TokenExpiration.startMonitoring();
        // Sign in to Firebase Auth with user's email
        FirebaseAuthService.signInWithAppAuth(
          action.payload.id,
          action.payload.access_token
        ).then(() => {
          // Firebase Auth sign-in completed
        }).catch(error => {
          console.error('Failed to sign in to Firebase Auth:', error);
        });
        break;
      case types.MICROSOFT_LOGIN_SUCCESS:
        const microsoftUser = {
          ...action.payload,
          auth_method: 'microsoft'
        };
        localStorage.setItem('user', JSON.stringify(microsoftUser));
        draft.isAuthenticated = true;
        draft.loading = false;
        draft.token = action.payload.access_token;
        draft.user = microsoftUser;
        draft.authMethod = 'microsoft';
        draft.loginFailed = false;
        // Start monitoring token expiration
        TokenExpiration.startMonitoring();
        // Sign in to Firebase Auth with user's email
        FirebaseAuthService.signInWithAppAuth(
          action.payload.id,
          action.payload.access_token
        ).then(() => {
          // Firebase Auth sign-in completed
          // Navigation is handled by the Login component's isAuthenticated effect
        }).catch(error => {
          console.error('Failed to sign in to Firebase Auth:', error);
        });
        break;
      case types.ENTERPRISE_SSO_LOGIN_SUCCESS:
        const enterpriseUser = {
          ...action.payload,
          auth_method: 'enterprise_sso'
        } as IUser;
        localStorage.setItem('user', JSON.stringify(enterpriseUser));
        draft.isAuthenticated = true;
        draft.loading = false;
        draft.token = action.payload.access_token;
        draft.user = enterpriseUser;
        draft.authMethod = 'enterprise_sso';
        draft.loginFailed = false;
        TokenExpiration.startMonitoring();
        FirebaseAuthService.signInWithAppAuth(
          action.payload.id,
          action.payload.access_token
        ).catch(error => {
          console.error('Failed to sign in to Firebase Auth:', error);
        });
        // Navigation is handled by AuthCallbackPage's isAuthenticated effect
        break;
      case types.LOGIN_FAILED:
        draft.loginFailed = true;
        draft.errorMessage = action.payload || "Login failed";
        localStorage.removeItem('user');
        draft.token = null;
        draft.isAuthenticated = false;
        draft.loading = false;
        break;
      case types.MICROSOFT_LOGIN_FAILED:
        localStorage.removeItem('user');
        draft.loginFailed = true;
        draft.errorMessage = action.payload || "Microsoft login failed";
        draft.token = null;
        draft.isAuthenticated = false;
        draft.loading = false;
        break;
      case types.ENTERPRISE_SSO_LOGIN_FAILED:
        localStorage.removeItem('user');
        draft.loginFailed = true;
        draft.errorMessage = action.payload || 'Enterprise SSO login failed';
        draft.token = null;
        draft.isAuthenticated = false;
        draft.loading = false;
        break;
      case types.CLEAR_AUTH_ERROR:
        draft.errorMessage = "";
        draft.loginFailed = false;
        break;
      case types.AUTH_ERROR:
      case types.REGISTER_FAILED:
        localStorage.removeItem('user');
        draft.token = null;
        draft.isAuthenticated = false;
        draft.loading = false;
        break;
      case types.LOGOUT:
      case types.TOKEN_EXPIRED:
        localStorage.removeItem('user');
        draft.token = null;
        draft.user = emptyAuthUser;
        draft.isAuthenticated = false;
        draft.loading = false;
        draft.authMethod = null;
        // Stop monitoring token expiration
        TokenExpiration.stopMonitoring();
        // Sign out from Firebase Auth (fire and forget)
        FirebaseAuthService.signOut().then(() => {
          // Firebase Auth sign-out completed
        }).catch(error => {
          console.error('Failed to sign out from Firebase Auth:', error);
        });
        break;
      default:
        return state;
    }
  });
